import { useTranslation } from '@mukil/i18n';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@jumpstone/ui-react';
import { NAV_ENTRIES, is_module_active } from './nav_model';
import type { NavEntry } from './nav_model';
import { NavBell } from './nav_bell';
import { AccountMenu } from './account_menu';
import { WageProAppIcon } from './wagepro_app_icon';
import { useSession } from '../../data/use_session';
import { navigate } from '../../lib/router';
import type { Route } from '../../lib/router';

/**
 * Admin app bar: brand, module navigation, notifications, account.
 *
 * There is deliberately no search box here. The only searchable surface is
 * the Employees -> Directory list, so that screen owns its own search input
 * (`employee_directory_screen.tsx`). A global box in this bar showed on every
 * module while searching nothing but the directory.
 */
export function TopNav({
  route,
  pending_approvals,
  on_open_mobile_menu,
}: {
  route: Route;
  pending_approvals: number;
  on_open_mobile_menu: () => void;
}): React.JSX.Element {
  const { t } = useTranslation('wp');
  const { session } = useSession();

  return (
    <header className="wp-topbar">
      <WageProAppIcon alt={t('brand.logo_alt')} className="shrink-0" />
      <span className="wp-wordmark">{t('common.app_name')}</span>

      {/* `.btn` is inline-flex, so the label is an anonymous flex item and
          `text-overflow` (not an inherited property) never reaches it — a
          `truncate` on the Button itself clips mid-word with no ellipsis.
          The label needs to be a real element for truncation to work. */}
      <Button
        variant="ghost"
        size="sm"
        className="min-w-0 max-w-[200px] xl:max-w-[300px]"
      >
        {/* The company name is tenant data, not a translatable string — it
            renders verbatim in every locale. */}
        <span className="truncate">{session.company_name}</span>
      </Button>

      {/* The wrapper carries the responsive display, not the Button: `.btn`
          sets `display` unlayered, which outranks a Tailwind display utility. */}
      <span className="lg:hidden">
        <Button
          variant="ghost"
          size="sm"
          aria-label={t('nav.open_menu')}
          onClick={on_open_mobile_menu}
        >
          <svg
            width="17"
            height="17"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </Button>
      </span>

      <nav
        aria-label={t('nav.primary')}
        className="hidden lg:flex items-center gap-0.5 ms-2 min-w-0 overflow-x-auto no-scrollbar"
      >
        {NAV_ENTRIES.map((entry) => (
          <NavEntryItem
            key={entry.id}
            entry={entry}
            active={is_module_active(route, entry.id)}
          />
        ))}
      </nav>

      <div className="ms-auto flex items-center gap-1.5 shrink-0">
        <NavBell pending_count={pending_approvals} />
        <AccountMenu />
      </div>
    </header>
  );
}

function NavEntryItem({
  entry,
  active,
}: {
  entry: NavEntry;
  active: boolean;
}): React.JSX.Element {
  const { t } = useTranslation('wp');
  const className = active ? 'wp-nav-active' : undefined;

  if (entry.sections.length === 0) {
    return (
      <Button
        variant="ghost"
        size="sm"
        className={className}
        aria-current={active ? 'page' : undefined}
        onClick={() => {
          navigate(entry.route);
        }}
      >
        {t(entry.label_key)}
      </Button>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={className}
          aria-current={active ? 'page' : undefined}
        >
          {t(entry.label_key)}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-[200px]">
        {entry.sections.map((section, section_index) => (
          <div key={section.heading_key ?? `section-${String(section_index)}`}>
            {section_index > 0 && <DropdownMenuSeparator />}
            {section.heading_key !== undefined && (
              <DropdownMenuLabel>{t(section.heading_key)}</DropdownMenuLabel>
            )}
            {section.items.map((item) => (
              <DropdownMenuItem
                key={item.id}
                onSelect={() => {
                  navigate(item.route);
                }}
              >
                {t(item.label_key)}
              </DropdownMenuItem>
            ))}
          </div>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
